import * as React from "react";

/**
 * A resolved item in the pagination row — either a clickable page number or a
 * truncation gap. Discriminated on `type` so consumers (and `SmartPagination`)
 * can render each branch with a stable React key.
 */
export type PaginationItem =
  | { type: "page"; page: number; active: boolean }
  | { type: "ellipsis"; key: "start" | "end" };

export interface UsePaginationOptions {
  /** The currently active page (1-based). */
  currentPage: number;
  /** Total number of pages. */
  totalPages: number;
  /**
   * How many page numbers to show on each side of the current page. Default
   * `1` reproduces the donor's `windowSize = 3` (current ± one neighbour).
   */
  siblingCount?: number;
  /**
   * How many pages to pin at each end. Default `1` reproduces the donor's
   * single first/last pin.
   */
  boundaryCount?: number;
}

/**
 * usePagination — pure sliding-window math for a smart pager, returned as a
 * typed item list ready to map straight onto the stock `Pagination` primitives.
 *
 * Ported verbatim from FE-React's `SlidingPagination` windowing (the behaviour
 * that 8 sites reimplemented by hand) and lifted into a hook so the math has a
 * single seam that is testable in isolation, with no DOM. SSR-safe: no
 * `window` / `document`, no side effects, just a memoized computation.
 *
 * Behaviour preserved exactly:
 * - Returns `[]` when `totalPages <= 1` (the component self-hides on this).
 * - With the defaults (`siblingCount = 1`, `boundaryCount = 1`): if
 *   `totalPages <= windowSize + 2` every page is rendered with no ellipsis;
 *   otherwise page 1 and the last page are pinned, a window slides around the
 *   current page, an edge-expansion loop keeps the visible window at full
 *   `windowSize` near either end, and a start / end ellipsis appears only when
 *   it would replace more than one hidden page.
 *
 * @example
 *   const items = usePagination({ currentPage: 5, totalPages: 20 });
 *   // [ {page:1}, {ellipsis:"start"}, {page:4}, {page:5,active}, {page:6}, {ellipsis:"end"}, {page:20} ]
 */
export function usePagination({
  currentPage,
  totalPages,
  siblingCount = 1,
  boundaryCount = 1,
}: UsePaginationOptions): PaginationItem[] {
  return React.useMemo(() => {
    // Self-hide contract: nothing to page through.
    if (totalPages <= 1) return [];

    // `windowSize` is the count of pages shown in the sliding middle window.
    // siblingCount = 1 -> windowSize 3 (current plus one each side), matching
    // the donor's hardcoded `const windowSize = 3`.
    const windowSize = siblingCount * 2 + 1;

    const page = (n: number, active: boolean): PaginationItem => ({
      type: "page",
      page: n,
      active,
    });

    // Few enough pages that the window plus both pinned boundaries already
    // cover them all — render every page, no ellipsis. With the defaults this
    // is `windowSize + 2` == 5, the donor's exact threshold (line 89); the
    // `2 * boundaryCount` term is the leading + trailing pin.
    if (totalPages <= windowSize + boundaryCount * 2) {
      return Array.from({ length: totalPages }, (_, i) =>
        page(i + 1, i + 1 === currentPage),
      );
    }

    const items: PaginationItem[] = [];

    // Always pin the leading boundary pages (donor pins page 1).
    for (let n = 1; n <= boundaryCount; n++) {
      items.push(page(n, n === currentPage));
    }

    // Window around the current page, clamped inside the pinned boundaries.
    let windowStart = Math.max(boundaryCount + 1, currentPage - siblingCount);
    let windowEnd = Math.min(
      totalPages - boundaryCount,
      currentPage + siblingCount,
    );

    // Edge-expansion loop: when the current page sits near an end, grow the
    // window back toward the centre so the visible count stays at windowSize
    // (donor lines 101-109). Grow left first, else right, else stop.
    while (windowEnd - windowStart + 1 < windowSize) {
      if (windowStart > boundaryCount + 1) {
        windowStart -= 1;
      } else if (windowEnd < totalPages - boundaryCount) {
        windowEnd += 1;
      } else {
        break;
      }
    }

    // Start ellipsis only when it replaces more than one hidden page (donor
    // line 112) — otherwise the single skipped page would just be shown.
    if (windowStart > boundaryCount + 1) {
      items.push({ type: "ellipsis", key: "start" });
    }

    for (let n = windowStart; n <= windowEnd; n++) {
      items.push(page(n, n === currentPage));
    }

    // End ellipsis under the same gap-aware rule (donor line 122).
    if (windowEnd < totalPages - boundaryCount) {
      items.push({ type: "ellipsis", key: "end" });
    }

    // Always pin the trailing boundary pages (donor pins `totalPages`).
    for (let n = totalPages - boundaryCount + 1; n <= totalPages; n++) {
      items.push(page(n, n === currentPage));
    }

    return items;
  }, [currentPage, totalPages, siblingCount, boundaryCount]);
}
